// Baselayer Results Explorer — defensive client-side parsing of the
// `rawResponseJson` payloads (KYB result + add-on results). Every field below
// is UNVERIFIED against the live Baselayer schema (docs are 403), so each
// reader is optional-chained and falls back to null/[]; nothing throws on a
// missing or differently-shaped field. Renderers show "—" / empty states when
// a value is absent.

/** Best-effort JSON.parse — returns null on any failure (never throws). */
export function safeParse(raw: string | null | undefined): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Narrow an unknown to a plain object (or null). */
export function asObject(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/** Narrow an unknown to an array (or []). */
export function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

export function asString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

export function asNumber(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function asBool(v: unknown): boolean | null {
  return typeof v === "boolean" ? v : null;
}

/** Reads the first present key from an object (handles UNVERIFIED key spellings). */
export function pick(
  obj: Record<string, unknown> | null,
  ...keys: string[]
): unknown {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] != null) return obj[k];
  }
  return undefined;
}

/** Display fallback for a string-ish value. */
export const dash = "—";

export function display(v: unknown): string {
  const s = asString(v) ?? (asNumber(v) != null ? String(v) : null);
  return s ?? dash;
}

// ---- KYB result shape -----------------------------------------------

export interface ParsedScore {
  type: string;
  rating: string | null;
  reasons: string[];
}

export interface ParsedKyb {
  /** The whole parsed payload, for tab-specific deep reads. */
  raw: Record<string, unknown> | null;
  /** The business object inside the payload (where present). */
  business: Record<string, unknown> | null;
  name: string | null;
  verified: boolean | null;
  nameMatch: boolean | null;
  addressMatch: boolean | null;
  tinMatched: boolean | null;
  scores: ParsedScore[];
}

/** Parses a KYB result's rawResponseJson into the shape the explorer reads. */
export function parseKyb(rawResponseJson: string | null | undefined): ParsedKyb {
  const root = asObject(safeParse(rawResponseJson));
  const business = asObject(pick(root, "business")) ?? root;
  const scoresRaw = asArray(pick(root, "scores") ?? pick(business, "scores"));

  const scores: ParsedScore[] = scoresRaw.map((s) => {
    const o = asObject(s);
    return {
      type: (asString(pick(o, "type", "kind", "name")) ?? "").toLowerCase(),
      rating: asString(pick(o, "rating", "grade", "letter", "value")),
      reasons: asArray(pick(o, "reasons", "reason_codes", "details"))
        .map((r) => {
          const ro = asObject(r);
          return ro
            ? asString(pick(ro, "description", "reason", "text", "message"))
            : asString(r);
        })
        .filter((x): x is string => !!x),
    };
  });

  return {
    raw: root,
    business,
    name: asString(pick(business, "name", "legal_name", "business_name")),
    verified: asBool(pick(root, "verified") ?? pick(business, "verified")),
    nameMatch: asBool(pick(root, "business_name_match", "name_match")),
    addressMatch: asBool(pick(root, "business_address_match", "address_match")),
    tinMatched: asBool(pick(root, "tin_matched", "tin_match")),
    scores,
  };
}

/** Finds a score row by type (case-insensitive contains). */
export function findScore(scores: ParsedScore[], type: string): ParsedScore | null {
  const needle = type.toLowerCase();
  return scores.find((s) => s.type.includes(needle)) ?? null;
}

// ---- Docket (litigations + bankruptcies share ONE add-on) ------------

export interface ParsedDocket {
  id: string | null;
  title: string | null;
  docketNumber: string | null;
  court: string | null;
  dateFiled: string | null;
  caseType: string | null;
  status: string | null;
  isBankruptcy: boolean;
  bankruptcyType: string | null;
  matchLevel: string | null;
}

export function parseDockets(rawResponseJson: string | null | undefined): ParsedDocket[] {
  const root = asObject(safeParse(rawResponseJson));
  const list = asArray(
    pick(root, "dockets", "results", "items", "data") ?? safeParse(rawResponseJson),
  );
  return list.map((d) => {
    const o = asObject(d);
    return {
      id: asString(pick(o, "id", "docket_id")),
      title: asString(pick(o, "title", "name", "case_name")),
      docketNumber: asString(pick(o, "docket_number", "case_number")),
      court: asString(pick(o, "court", "court_name")),
      dateFiled: asString(pick(o, "date_filed", "filing_date", "filed_date")),
      caseType: asString(pick(o, "case_type", "type", "nature_of_suit")),
      status: asString(pick(o, "status", "normalized_status")),
      isBankruptcy: asBool(pick(o, "is_bankruptcy")) ?? false,
      bankruptcyType: asString(pick(o, "bankruptcy_type")),
      matchLevel: asString(pick(o, "match_level", "match")),
    };
  });
}

// ---- Liens -----------------------------------------------------------

export interface ParsedLienParty {
  role: string | null;
  name: string | null;
  address: string | null;
}

export interface ParsedLien {
  id: string | null;
  status: string | null;
  filingType: string | null;
  filingNumber: string | null;
  filingDate: string | null;
  lapseDate: string | null;
  state: string | null;
  lapsed: boolean | null;
  debtors: ParsedLienParty[];
  securedParties: ParsedLienParty[];
  collateral: string | null;
  amendments: Record<string, unknown>[];
  documentFilename: string | null;
}

function parseLienParty(v: unknown): ParsedLienParty {
  const o = asObject(v);
  return {
    role: asString(pick(o, "role", "party_type")),
    name: asString(pick(o, "name", "party_name")),
    address: asString(pick(o, "address", "full_address")),
  };
}

export function parseLiens(rawResponseJson: string | null | undefined): ParsedLien[] {
  const root = asObject(safeParse(rawResponseJson));
  const list = asArray(
    pick(root, "filings", "liens", "results", "items") ?? safeParse(rawResponseJson),
  );
  return list.map((f) => {
    const o = asObject(f);
    const parties = asArray(pick(o, "parties"));
    const explicitDebtors = asArray(pick(o, "debtors"));
    const explicitSecured = asArray(pick(o, "secured_parties"));
    const debtors = explicitDebtors.length
      ? explicitDebtors.map(parseLienParty)
      : parties
          .map(parseLienParty)
          .filter((p) => (p.role ?? "").toLowerCase().includes("debtor"));
    const securedParties = explicitSecured.length
      ? explicitSecured.map(parseLienParty)
      : parties
          .map(parseLienParty)
          .filter((p) => (p.role ?? "").toLowerCase().includes("secured"));
    return {
      id: asString(pick(o, "id", "filing_id", "filing_number")),
      status: asString(pick(o, "status")),
      filingType: asString(pick(o, "filing_type", "lien_type", "type")),
      filingNumber: asString(pick(o, "filing_number")),
      filingDate: asString(pick(o, "filing_date", "date_filed")),
      lapseDate: asString(pick(o, "lapse_date", "lapsed_date")),
      state: asString(pick(o, "state", "filing_state", "jurisdiction")),
      lapsed: asBool(pick(o, "lapsed")),
      debtors,
      securedParties,
      collateral: asString(pick(o, "collateral", "collateral_statement")),
      amendments: asArray(pick(o, "amendments"))
        .map(asObject)
        .filter((a): a is Record<string, unknown> => !!a),
      documentFilename: asString(pick(o, "document_filename")),
    };
  });
}

// ---- Watchlists ------------------------------------------------------

export interface ParsedWatchlist {
  code: string | null;
  name: string | null;
  count: number;
  details: { program: string | null; remarks: string | null }[];
}

export function parseWatchlists(
  rawResponseJson: string | null | undefined,
): ParsedWatchlist[] {
  const root = asObject(safeParse(rawResponseJson));
  const list = asArray(pick(root, "watchlist_hits", "watchlists"));
  return list.map((w) => {
    const o = asObject(w);
    return {
      code: asString(pick(o, "code", "list_code")),
      name: asString(pick(o, "name", "list_name")),
      count: asNumber(pick(o, "count", "hit_count")) ?? 0,
      details: asArray(pick(o, "details", "hits")).map((d) => {
        const dobj = asObject(d);
        return {
          program: asString(pick(dobj, "program", "programs")),
          remarks: asString(pick(dobj, "remarks", "notes", "description")),
        };
      }),
    };
  });
}
